import React, { useContext, useEffect, useRef, useState } from "react";
import { generate } from "short-uuid";
import {
  LinerServiceAdminContext,
  LinerServiceAdminSettings,
} from "./linerServiceAdmin";
import { Route } from "./linerServiceAdminRoute";
import TypeaheadSearch from "./typeaheadSearch";
import { TypeaheadSearchResultItem } from "./typeaheadSearchResult";

export interface LinerServiceFrequencySelectorProps {
  id: string;
  valueSelected: Function;
  defaultValue?: TypeaheadSearchResultItem | null | undefined;
}

export interface Frequency {
  amount: TypeaheadSearchResultItem | null;
  unit: TypeaheadSearchResultItem | null;
}

const units: TypeaheadSearchResultItem[] = [
  { id: generate(), value: "hour", label: "Timma" },
  { id: generate(), value: "month", label: "Månad" },
  { id: generate(), value: "week", label: "Vecka" },
  { id: generate(), value: "day", label: "Dag" },
  { id: generate(), value: "year", label: "År" },
];

const LinerServiceFrequencySelector = ({
  id,
  valueSelected,
  defaultValue,
}: LinerServiceFrequencySelectorProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [frequency, setFrequency] = useState<Frequency>({
    amount: null,
    unit: null,
  });
  const [defaultAmount, setDefaultAmount] =
    useState<TypeaheadSearchResultItem | null>(null);
  const [defaultUnit, setDefaultUnit] =
    useState<TypeaheadSearchResultItem | null>(null);

  useEffect(() => {
    if (id) {
      setFrequency({ amount: null, unit: null });
      setDefaultAmount(null);
      setDefaultUnit(null);
    }
  }, [id]);

  useEffect(() => {
    if (defaultValue) {
      const defaultValues = defaultValue.label.split("/");

      if (!defaultAmount && !defaultUnit) {
        setDefaultAmount({
          id: generate(),
          label: defaultValues[0],
          value: defaultValues[0],
        });
        setDefaultUnit({
          id: generate(),
          label: defaultValues[1],
          value: defaultValues[1],
        });
      }
    }
  }, [defaultValue]);

  useEffect(() => {
    if (defaultAmount && defaultUnit) {
      setFrequency({
        ...frequency,
        ...{ amount: defaultAmount, unit: defaultUnit },
      });
    }
  }, [defaultAmount, defaultUnit]);

  useEffect(() => {
    if (frequency.amount && frequency.unit) {
      const frequencyString = `${frequency.amount.label}/${frequency.unit.label}`;
      valueSelected({ label: frequencyString, value: frequencyString });
    }
  }, [frequency]);

  const setUnit = (item: TypeaheadSearchResultItem) => {
    setFrequency({ ...frequency, ...{ unit: item } });
  };

  const setAmount = (item: TypeaheadSearchResultItem) => {
    if (!isNaN(Number(item.label))) {
      setFrequency({ ...frequency, ...{ amount: item } });
    }
  };

  return (
    <div ref={ref} className={`frequency-selector`}>
      <div className="frequency-selector-controls">
        <TypeaheadSearch
          id={`amount_${id}`}
          type="number"
          maxWidth={55}
          minWidth={30}
          min={0}
          resultList={[]}
          defaultValue={defaultAmount}
          valueSelected={(item: TypeaheadSearchResultItem) => setAmount(item)}
        />

        <TypeaheadSearch
          id={`unit_${id}`}
          resultList={units}
          maxWidth={100}
          minWidth={80}
          defaultValue={defaultUnit || units[0]}
          valueSelected={(item: TypeaheadSearchResultItem) => setUnit(item)}
        />
      </div>
    </div>
  );
};

export default LinerServiceFrequencySelector;
